import React, { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import moment from "moment";
import { isWeekday } from "helpers/commonHelpers";
import { useQueryClient } from "@tanstack/react-query";

import "./Quotation.scss";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import errorToast from "helpers/errorToast";
import Dialog from "components/organisms/Dialog/Dialog";
import FormField from "components/molecules/FormField/FormField";
import Radio from "components/atoms/Radio/Radio";
import Textarea from "components/atoms/Textarea/Textarea";
import Button from "components/atoms/Button/Button";
import { approveQuotation, getQuotationBookingForm } from "api/services/quotation";
import Alert from "components/molecules/Alert/Alert";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import CustomsBrokerDialog from "./CustomsBrokerDialog";

function ConfirmBookQuoteDialog({
  requestData,
  quotationId,
  isBookAsDuplicate,
  close,
  show,
  setShowBookQuoteDialog,
  fetchQuoteRequestData,
  shipmentId,
  customBrokerAvailable,
  showCustomsBroker,
  setShowCustomsBroker,
  bookingType,
  status,
  isApprovalRequested,
  isFedexPostmenQuotation,
  isFedexDirectIntegration,
}) {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, getValues, watch, setValue, trigger } = methods;
  watch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputs, setInputs] = useState({});
  const [bookingSurvey, setBookingSurvey] = useState({});
  const [customsBrokersCompleted, setCustomsBrokersCompleted] = useState(false);
  const [declaredValueCurrency, setDeclaredValueCurrency] = useState({});

  const getInputs = () => {
    setIsLoading(true);
    // If the booking type is not a direct-booking, we need to add a query parameter to the request
    const query = bookingType && `?${bookingType}=true`;
    getQuotationBookingForm(quotationId, query)
      .then((res) => {
        if (res.data.booking_survey_items) {
          setBookingSurvey(res.data.booking_survey_items);
          delete res.data.booking_survey_items;
        }
        /** Filter declared_value_for_carriage & declared_value_currency key value from data response */
        const currencyDefaultKeys = ["declared_value_for_carriage", "declared_value_currency"];
        const filteredObject = Object.keys(res.data)
          ?.filter((key) => currencyDefaultKeys.includes(key))
          ?.reduce((obj, key) => {
            obj[key] = res.data[key];
            return obj;
          }, {});
        setDeclaredValueCurrency(filteredObject);
        /** Delete declared_value_for_carriage & declared_value_currency key value from object and render default by using declaredValueCurrency */
        delete res.data.declared_value_for_carriage;
        delete res.data.declared_value_currency;
        setInputs(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        close();
        errorToast(err, "Could not retrieve task. Please try again later.");
      });
  };

  useEffect(() => {
    // setValue("parcel_pickup", "regular_pickup");
    if (status !== "reapproval_needed_after_booking") getInputs();
  }, []);

  // Formatting options because our custom components expect different key value pairs.
  const formatOptions = (options, isRadio = false) =>
    options.map((option) => ({
      label: option.key,
      value: isRadio ? option.value.toString() : option.value,
    }));

  const getPickupDateMaxOption = () => {
    if (isFedexDirectIntegration && isFedexPostmenQuotation) return 13;
    if (isFedexPostmenQuotation) return 1;
    return 3;
  };

  const pickupDateMaxOption = getPickupDateMaxOption();

  const confirmBooking = (data) => {
    setIsSubmitting(true);
    // Format booking surveys from the data
    const bookingSurveys = Object.keys(data).reduce((acc, key) => {
      if (key.startsWith("survey_item_") && key.endsWith("_value")) {
        const surveyId = key.split("_")[2];
        acc.push({
          question: parseInt(surveyId),
          response: data[key],
        });
        delete data[key];
      }
      if (key.startsWith("survey_item_") && key.endsWith("_comment")) {
        const surveyId = key.split("_")[2];
        const survey = acc.find((survey) => survey.question === parseInt(surveyId));
        survey.yes_value = data[key];
        delete data[key];
      }
      return acc;
    }, []);

    if (data.pickup_date) data.pickup_date = moment(data.pickup_date).format("MM/DD/YYYY");
    if (data.parcel_pickup === "regular_pickup") {
      delete data.pickup_date;
      delete data.pickup_time_from;
      delete data.pickup_time_to;
    }

    data.booking_survey_items = bookingSurveys;
    data.booking_survey_items = data.booking_survey_items.filter((item) => item.response !== "");

    Object.keys(data).forEach((key) => {
      if (data[key] === "") delete data[key];
    });

    const payload = {
      ...requestData,
      ...data,
    };

    approveQuotation(quotationId, payload)
      .then((res) => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
        const toastMessage = bookingType ? (
          <FormattedMessage
            id="shipment-quotes.components.bookQuoteDialog.approvalRequested"
            defaultMessage="Approval requested"
          />
        ) : (
          <FormattedMessage
            id="shipment-quotes.components.bookQuoteDialog.bookingConfirmed"
            defaultMessage="Booking confirmed"
          />
        );
        toast(toastMessage, {
          type: "success",
        });
        if (!res.data.further_approval_needed) {
          navigate(`/shipment/${res.data.shipment_id}/tracking`);
          queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(res.data.shipment_id) });
        } else {
          fetchQuoteRequestData();
          close();
        }
      })
      .catch((err) => {
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.requestApprovalDialog.couldNotSubmitApprovalRequest"
            defaultMessage="Could not submit approval request. Please try again later."
          />,
        );
        queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(shipmentId) });
      })
      .finally(() => setIsSubmitting(false));
  };

  const pickupTimeValidation = (value) => {
    const parsedValue = moment(value, "HH:mm");
    const isValid = parsedValue.isBetween(moment("08:00", "HH:mm"), moment("18:00", "HH:mm"));
    return isValid || "Time must be between 08:00 and 18:00";
  };

  const renderInputType = (input) => {
    const initialSelection = input.initial_selection || input.initial_comment || input.initial_data;
    const disableField = Boolean(initialSelection);
    switch (input.type) {
      case "textarea":
        return (
          <Textarea
            name={input.field_id}
            type={input.type}
            placeholder={input.placeholder}
            required={input.required}
            defaultValue={initialSelection}
            eventContext="Booking Confirmation Dialog"
            disabled={disableField}
          />
        );
      case "textinput":
        return (
          <div className="input-element w-192">
            <FormField
              field_id={input.field_id}
              type={input.type}
              eventContext="Booking Confirmation Dialog"
              placeholder={input.placeholder || "Please enter"}
              mandatory={input.mandatory}
              defaultValue={initialSelection}
              disabled={disableField}
            />
          </div>
        );
      case "number":
        return (
          <div className="input-element w-192">
            <FormField
              onlyNumber
              field_id={input.field_id}
              type={input.type}
              eventContext="Booking Confirmation Dialog"
              placeholder={input.placeholder}
              mandatory={input.mandatory}
              defaultValue={initialSelection}
              disabled={disableField}
            />
          </div>
        );
      case "dropdown":
        return (
          <div className="w-192">
            <Dropdown
              useHookForm
              placeholder={input.placeholder || "Please select"}
              name={input.field_id}
              required={input.mandatory}
              options={formatOptions(input.dropdown_values)}
              defaultValue={initialSelection}
              disabled={disableField}
            />
          </div>
        );
      case "radio":
        // Set the radio to the initial selection if it exists
        if (initialSelection && !getValues(input.field_id)) setValue(input.field_id, initialSelection?.toString?.());
        // Otherwise, if the radio has only two options and the first one is "Yes" and the second one is "No", select "No"
        else if (input.options[0].key === "Yes" && !initialSelection && !getValues(input.field_id))
          setValue(input.field_id, "Yes");

        if (input.field_id === "parcel_pickup" && !getValues(input.field_id)) setValue("parcel_pickup", "regular_pickup");

        return (
          <Radio
            customColClass="p-0"
            name={input.field_id}
            options={formatOptions(input.options, true)}
            required={input.mandatory}
            className="border-0 pt-0 pb-0"
            defaultChecked={getValues(input.field_id)?.toString?.()}
            eventContext="Booking Confirmation Dialog"
            disabled={disableField}
          />
        );
      case "date":
        return (
          <DatePicker
            disabled={disableField}
            minDate={new Date()}
            defaultValue={new Date(initialSelection)}
            eventContext="Booking Confirmation Dialog"
            name={input.field_id}
            wrapperClassNames="mr-2"
            onDateSelect={() => trigger(input.field_id)}
            required={input.mandatory}
            {...(input.field_id === "pickup_date" && {
              maxDate: moment().add(pickupDateMaxOption, "days").toDate(),
              validations: {
                _dpCustomValidation: (val) =>
                  isWeekday(moment(val).startOf("day")) || "Weekends are not available for pickup",
              },
            })}
          />
        );
      case "time":
        return (
          <TimeInput
            disabled={disableField}
            eventContext="Booking Confirmation Dialog"
            name={input.field_id}
            outputFormat="HH:mm"
            required={input.mandatory}
            defaultValue={initialSelection || "09:00"}
            validations={!isFedexDirectIntegration ? { pickupTimeValidation } : undefined}
          />
        );
      default:
        return null;
    }
  };

  const renderLabel = (input) => {
    if (input.field_id === "recipient_payee_account_number") {
      if (getValues("shipment_paid_by") === "recipient") {
        return intl.formatMessage({
          id: "shipment-quotes.components.bookQuoteDialog.recipientPayeeLabel",
          defaultMessage: "Recipient payee account number",
        });
      }
      if (getValues("shipment_paid_by") === "third_party") {
        return intl.formatMessage({
          id: "shipment-quotes.components.bookQuoteDialog.thirdPartyPayeeLabel",
          defaultMessage: "Third party payee account number",
        });
      }
    }
    return input.label;
  };

  // Creates a row with a label and input.
  const renderInput = (input) => {
    const conditionalPickupInputs = ["pickup_date", "pickup_time_from", "pickup_time_to"];

    if (getValues("parcel_customs_paid_by") !== "recipient" && input.field_id === "customs_payee_account_number") return;
    if (
      !["recipient", "third_party"].includes(getValues("shipment_paid_by")) &&
      input.field_id === "recipient_payee_account_number"
    )
      return;
    if (getValues("parcel_pickup") === "regular_pickup" && conditionalPickupInputs.includes(input.field_id)) return;

    return (
      <Row style={{ marginBottom: "20px" }}>
        <Col>
          <p className="pl-0 d-flex flex-column">
            <span className={`font-weight-bold ${input.mandatory && "required"}`}> {renderLabel(input)}</span>
            {input.additional_information !== "" && (
              <span className="text-secondary"> ({input.additional_information})</span>
            )}
          </p>
        </Col>
        <Col className="pr-0 pl-0">
          {renderInputType(input)}
          {getValues(input.field_id) === "Yes" && input.type === "radio" && (
            <div className="input-element w-192">
              <FormField
                field_id={`${input.field_id}_comment`}
                defaultValue={input.initial_comment}
                eventContext="Booking Confirmation Dialog"
                placeholder={input.comment_placeholder}
                mandatory={input.is_comment_mandatory}
                disabled={!!input.initial_comment}
              />
            </div>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <>
      {customBrokerAvailable && (
        <CustomsBrokerDialog
          setShowBookQuoteDialog={setShowBookQuoteDialog}
          isApprovalRequested={isApprovalRequested}
          show={showCustomsBroker}
          setCustomsBrokersCompleted={setCustomsBrokersCompleted}
          shipmentId={shipmentId}
          close={() => setShowCustomsBroker(false)}
        />
      )}
      {(customsBrokersCompleted || !customBrokerAvailable) && (
        <Dialog
          size="md"
          title={intl.formatMessage({
            id: "tracking.bookingDetails",
            defaultMessage: "Booking Details",
          })}
          show={show}
          onHide={close}
        >
          {isLoading && (
            <div className="text-center d-flex align-items-center justify-content-center" style={{ height: "400px" }}>
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          {!isLoading && (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(confirmBooking)}>
                {isBookAsDuplicate && (
                  <div style={{ width: "500px" }}>
                    <Alert variant="warning" className="mb-4">
                      <FormattedMessage
                        id="shipment-quotes.components.bookQuoteDialog.warningMessage"
                        defaultMessage="You will create a shipment with a new shipment ID, please make sure your logistics provider is aware and able to provide the selected service."
                      />
                    </Alert>
                  </div>
                )}
                {Object.keys(bookingSurvey).length > 0 &&
                  bookingSurvey.map((item) => <React.Fragment key={item.field_id}>{renderInput(item)}</React.Fragment>)}
                {declaredValueCurrency?.declared_value_for_carriage?.field_id === "declared_value" && (
                  <Row style={{ marginBottom: "20px" }}>
                    <Col>
                      <span
                        className={`font-weight-bold ${
                          declaredValueCurrency?.declared_value_for_carriage?.field_id?.mandatory && "required"
                        }`}
                      >
                        Declared value for carriage
                      </span>
                    </Col>
                    <Col className="pr-0 pl-0">
                      {declaredValueCurrency?.declared_value_for_carriage?.field_id === "declared_value" && (
                        <div className="d-flex align-items-center gap-4">
                          <div className="input-element" style={{ width: "90px" }}>
                            <FormField
                              onlyNumber
                              field_id={declaredValueCurrency.declared_value_for_carriage.field_id}
                              type={declaredValueCurrency.declared_value_for_carriage.type}
                              eventContext="Booking Confirmation Dialog"
                              placeholder={declaredValueCurrency.declared_value_for_carriage.placeholder}
                              mandatory={declaredValueCurrency.declared_value_for_carriage.mandatory}
                              defaultValue={declaredValueCurrency.declared_value_for_carriage.initial_data}
                              disabled={!!declaredValueCurrency.declared_value_for_carriage.initial_data}
                            />
                          </div>
                          <div className="ml-2 w-95">
                            {declaredValueCurrency?.declared_value_currency?.field_id === "declared_value_currency" && (
                              <SearchSelect
                                options={formatOptions(declaredValueCurrency.declared_value_currency.dropdown_values)?.sort(
                                  (a, b) => a?.value?.localeCompare(b?.value),
                                )}
                                placeholder={declaredValueCurrency.declared_value_currency.placeholder || "Please select"}
                                name={declaredValueCurrency.declared_value_currency.field_id}
                                required={declaredValueCurrency.declared_value_currency.mandatory}
                                defaultValue={declaredValueCurrency.declared_value_currency.initial_selection}
                                disable={!!declaredValueCurrency.declared_value_currency.initial_selection}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>
                )}
                {Object.keys(inputs).length > 0 &&
                  Object.keys(inputs).map((item) => (
                    <React.Fragment key={inputs[item].field_id}>{renderInput(inputs[item])}</React.Fragment>
                  ))}
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-primary"
                    className="mr-2"
                    label={intl.formatMessage({
                      id: "general.cancel",
                      defaultMessage: "Cancel",
                    })}
                    onClick={close}
                  />
                  {customBrokerAvailable && (
                    <Button
                      variant="outline-primary"
                      label={intl.formatMessage({
                        id: "general.back",
                        defaultMessage: "Back",
                      })}
                      onClick={() => {
                        setShowCustomsBroker(true);
                        setCustomsBrokersCompleted(false);
                      }}
                    />
                  )}
                  <Button
                    className="ml-auto"
                    variant="primary"
                    label={intl.formatMessage({
                      id: "general.confirm",
                      defaultMessage: "Confirm",
                    })}
                    type="submit"
                    isLoading={isSubmitting}
                  />
                </div>
              </form>
            </FormProvider>
          )}
        </Dialog>
      )}
    </>
  );
}

ConfirmBookQuoteDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchQuoteRequestData: PropTypes.func,
  requestData: PropTypes.instanceOf(Object),
  isBookAsDuplicate: PropTypes.bool,
  shipmentId: PropTypes.string.isRequired,
  quotationSettings: PropTypes.instanceOf(Object).isRequired,
  showCustomsBroker: PropTypes.bool.isRequired,
  customBrokerAvailable: PropTypes.bool.isRequired,
  setShowCustomsBroker: PropTypes.func.isRequired,
  isApprovalRequest: PropTypes.bool,
  bookingType: PropTypes.oneOf(["suggest_approval", "request_approval", "transfer_approval", ""]),
  status: PropTypes.string.isRequired,
  isApprovalRequested: PropTypes.bool,
  setShowBookQuoteDialog: PropTypes.func.isRequired,
  isFedexPostmenQuotation: PropTypes.bool,
  isFedexDirectIntegration: PropTypes.bool,
};

ConfirmBookQuoteDialog.defaultProps = {
  fetchQuoteRequestData: () => {},
  requestData: {},
  isBookAsDuplicate: false,
  isApprovalRequest: false,
  bookingType: "",
  isApprovalRequested: false,
  isFedexPostmenQuotation: false,
  isFedexDirectIntegration: false,
};

export default ConfirmBookQuoteDialog;
